import React, { useState, useEffect } from 'react';
import DateTimeDisplay from './DateTimeDisplay';
import { API_ENDPOINTS } from '../config/api';

const SecurityManagement = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [settings, setSettings] = useState({
    security_auto_generate_days: [],
    security_auto_generate_time: '06:00'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadSecurityLogs();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/security/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading security settings:', error);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/security/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        alert('Settings saved successfully! Changes will take effect after server restart.');
        setShowSettings(false);
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleDay = (day) => {
    setSettings(prev => ({
      ...prev,
      security_auto_generate_days: prev.security_auto_generate_days.includes(day)
        ? prev.security_auto_generate_days.filter(d => d !== day)
        : [...prev.security_auto_generate_days, day].sort()
    }));
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const loadSecurityLogs = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/security/logs`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      console.error('Error loading security logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCodes = async () => {
    setGeneratingCodes(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/security/generate-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        alert('Security codes generated successfully for all students!');
        loadSecurityLogs();
      } else {
        alert('Failed to generate codes');
      }
    } catch (error) {
      console.error('Error generating codes:', error);
      alert('Failed to generate codes');
    } finally {
      setGeneratingCodes(false);
    }
  };

  const resetStudentCodes = async (username) => {
    if (!window.confirm(`Reset security codes for ${username}?`)) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/security/reset-codes/${username}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        alert('Codes reset successfully!');
        loadSecurityLogs();
      } else {
        alert('Failed to reset codes');
      }
    } catch (error) {
      console.error('Error resetting codes:', error);
      alert('Failed to reset codes');
    }
  };

  const getStatusBadge = (dropoffVerified, pickupVerified) => {
    if (dropoffVerified && pickupVerified) {
      return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Complete</span>;
    } else if (dropoffVerified || pickupVerified) {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">Partial</span>;
    } else {
      return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">Pending</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="text-center">Loading security management...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-blue-900">Security Management</h1>
        <DateTimeDisplay />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{logs.length}</div>
          <div className="text-sm text-gray-600">Total Students</div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            {logs.filter(l => l.dropoff_verified && l.pickup_verified).length}
          </div>
          <div className="text-sm text-gray-600">Complete Attendance</div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600">
            {logs.filter(l => l.dropoff_verified || l.pickup_verified).length}
          </div>
          <div className="text-sm text-gray-600">Partial Attendance</div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="text-2xl font-bold text-red-600">
            {logs.filter(l => !l.dropoff_verified && !l.pickup_verified).length}
          </div>
          <div className="text-sm text-gray-600">No Attendance</div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
        <h2 className="text-xl font-semibold text-blue-800 mb-4">Security Code Management</h2>

        {/* Auto-generation status */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Automatic Code Generation Active</h3>
              <p className="text-sm text-green-700 mt-1">
                Security codes are automatically generated daily at {settings.security_auto_generate_time || '06:00'} on {settings.security_auto_generate_days && settings.security_auto_generate_days.length > 0 ? settings.security_auto_generate_days.map(day => dayNames[day]).join(', ') : 'working days (Mon-Fri)'}.
              </p>
            </div>
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={generateCodes}
            disabled={generatingCodes}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition duration-300 disabled:opacity-50"
          >
            {generatingCodes ? 'Generating...' : 'Manual Generation'}
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 transition duration-300"
          >
            {showSettings ? 'Hide Settings' : 'Configure Auto-Generation'}
          </button>
          <button
            onClick={loadSecurityLogs}
            className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700 transition duration-300"
          >
            Refresh Logs
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Manual generation allows you to create codes immediately if needed. Codes are valid only for today.
        </p>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
          <h2 className="text-xl font-semibold text-blue-800 mb-4">Auto-Generation Settings</h2>

          <div className="space-y-6">
            {/* Time Setting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Generation Time (HH:MM)
              </label>
              <input
                type="time"
                value={settings.security_auto_generate_time}
                onChange={(e) => setSettings(prev => ({ ...prev, security_auto_generate_time: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Days Setting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days of the Week
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[0, 1, 2, 3, 4, 5, 6].map(day => (
                  <label key={day} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={settings.security_auto_generate_days.includes(day)}
                      onChange={() => toggleDay(day)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{dayNames[day]}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Changes require a server restart to take effect. The scheduler runs based on the saved configuration.
            </p>
          </div>
        </div>
      )}

      {/* Security Logs */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold text-blue-800 mb-4">Today's Security Logs</h2>

        {logs.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No security codes generated for today.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dropoff Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pickup Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {log.student_name}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {log.class_name}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      <span className={`font-mono ${log.dropoff_verified ? 'text-green-600' : 'text-gray-900'}`}>
                        {log.dropoff_code}
                        {log.dropoff_verified && (
                          <span className="ml-1 text-xs text-green-600">?</span>
                        )}
                      </span>
                      {log.dropoff_verified_at && (
                        <div className="text-xs text-gray-400">
                          {new Date(log.dropoff_verified_at).toLocaleTimeString()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      <span className={`font-mono ${log.pickup_verified ? 'text-green-600' : 'text-gray-900'}`}>
                        {log.pickup_code}
                        {log.pickup_verified && (
                          <span className="ml-1 text-xs text-green-600">?</span>
                        )}
                      </span>
                      {log.pickup_verified_at && (
                        <div className="text-xs text-gray-400">
                          {new Date(log.pickup_verified_at).toLocaleTimeString()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {getStatusBadge(log.dropoff_verified, log.pickup_verified)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      <button
                        onClick={() => resetStudentCodes(log.username)}
                        className="bg-yellow-600 text-white px-3 py-1 rounded text-xs hover:bg-yellow-700 transition duration-300"
                      >
                        Reset Codes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityManagement;
